import React, { useEffect, useState } from "react";
import { jt, msgid, ngettext, t } from "ttag";
import { format } from "date-fns";
import { BaseDialog, CheckMark, CopyIcon, IconButton, Table, Tooltip } from "@coolremote/shared";
import { useStoreActions, useStoreState } from "../../models/RootStore";
import { getDateFnsLocale } from "../../utils/dateUtils";
import {
  getAllSitePackageRows,
  getSiteUtcOffsetLabel,
  getSitesRequiringAttention,
  getStatusColor,
  ISitePackageRow,
  SitePackageStatusColor,
  toSiteDay,
} from "../../utils/SitePackagesUtils";
import styles from "./SitePackagesDialog.module.css";

export type SitePackagesDialogMode = "alert" | "status";

interface IProps {
  open: boolean;
  mode: SitePackagesDialogMode;
  /** Alert mode only: the sites to list, in display order (most urgent first). */
  siteIds?: string[];
  onClose: () => void;
}

// "Other" in the report form's topic list; a renewal request isn't a technical problem.
const REPORT_TOPIC_OTHER = 4;

const STATUS_CLASS: { [key in SitePackageStatusColor]: string } = {
  green: styles.statusGreen,
  yellow: styles.statusYellow,
  orange: styles.statusOrange,
  red: styles.statusRed,
  na: styles.statusNa,
};

const formatDate = (epochMs: number | null | undefined, timezone?: string) => {
  if (epochMs == null) {
    return t`N/A`;
  }
  const date = format(toSiteDay(epochMs, timezone), "P", { locale: getDateFnsLocale() });
  const utcOffset = getSiteUtcOffsetLabel(epochMs, timezone);
  if (!utcOffset) {
    return date;
  }
  return (
    <span>
      {date}{" "}
      <Tooltip light title={t`Site Time Zone: ${timezone}`}>
        <span className={styles.timezone}>({utcOffset})</span>
      </Tooltip>
    </span>
  );
};

const formatDaysLeft = (daysLeft: number | null) => {
  if (daysLeft == null) {
    return t`N/A`;
  }
  if (daysLeft === 0) {
    return t`Expires Today`;
  }
  if (daysLeft > 0) {
    return daysLeft;
  }
  const daysAgo = -daysLeft;
  return ngettext(msgid`Expired ${daysAgo} day ago`, `Expired ${daysAgo} days ago`, daysAgo);
};

const renderDaysLeft = (row: ISitePackageRow) => {
  const color = getStatusColor(row.daysLeft);
  return (
    <span className={`${styles.statusCell} ${STATUS_CLASS[color]}`}>
      <span className={styles.statusDot} />
      {formatDaysLeft(row.daysLeft)}
    </span>
  );
};

// Keeps the order the hook decided on (most urgent first) instead of the store's order.
const getAlertRows = (attentionRows: ISitePackageRow[], siteIds: string[]): ISitePackageRow[] =>
  siteIds.reduce<ISitePackageRow[]>((acc, siteId) => {
    const row = attentionRows.find(attentionRow => attentionRow.siteId === siteId);
    if (row) {
      acc.push(row);
    }
    return acc;
  }, []);

const COPIED_RESET_MS = 2000;

interface IContactEmailProps {
  email: string;
  siteNames: string[];
}

// The link opens a new message in the viewer's mail app; the copy button covers viewers without one.
// "Copied" shows inline because a toast would render underneath the modal dialog.
const ContactEmail: React.FC<IContactEmailProps> = ({ email, siteNames }) => {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) {
      return;
    }
    const timeout = setTimeout(() => setCopied(false), COPIED_RESET_MS);
    return () => clearTimeout(timeout);
  }, [copied]);

  const sites = siteNames.join(", ");
  const subject = encodeURIComponent(t`Professional app subscription renewal - ${sites}`);

  const handleCopy = () => {
    navigator.clipboard
      ?.writeText(email)
      .then(() => setCopied(true))
      .catch(() => undefined);
  };

  return (
    <span className={styles.contactEmail}>
      <a href={`mailto:${email}?subject=${subject}`} target="_blank" rel="noopener noreferrer">
        {email}
      </a>
      <IconButton
        size="small"
        title={copied ? t`Copied` : t`Copy email`}
        aria-label={t`Copy email`}
        icon={copied ? <CheckMark /> : <CopyIcon width={16} height={16} />}
        onClick={handleCopy}
      />
    </span>
  );
};

// The one contact email when every listed site has the same non-empty one; otherwise each site
// shows its own in the table.
const getSharedEmail = (rows: ISitePackageRow[]): string | undefined => {
  const email = rows[0]?.salesAccountEmail;
  if (!email || rows.some(row => row.salesAccountEmail !== email)) {
    return undefined;
  }
  return email;
};

const renderRenewalMessage = (link?: React.ReactNode) => {
  if (!link) {
    return t`Your Professional app subscription is due for renewal. To avoid any interruption to your service, please contact your CoolAutomation representative.`;
  }
  return jt`Your Professional app subscription is due for renewal. To avoid any interruption to your service, please contact your CoolAutomation representative at ${link}`;
};

const SitePackagesDialog: React.FC<IProps> = ({ open, mode, siteIds = [], onClose }) => {
  const sitesPackages = useStoreState(state => state.sites.sitesPackages);
  const allSites = useStoreState(state => state.sites.allSites);
  const getSiteName = useStoreState(state => state.sites.getSiteName);
  const getCustomerName = useStoreState(state => state.customers.getCustomerName);
  const { setGlobalErrorReport } = useStoreActions(actions => actions.errorMessage);

  const isAlertMode = mode === "alert";
  const rows = isAlertMode
    ? getAlertRows(getSitesRequiringAttention(sitesPackages, allSites), siteIds)
    : getAllSitePackageRows(sitesPackages, allSites);
  const title = isAlertMode ? t`Subscription Renewal Required` : t`Account Subscription Status`;
  const sharedEmail = getSharedEmail(rows);
  const showContactColumn = isAlertMode && !sharedEmail;

  const handleContactSupport = () => {
    const sites = rows
      .map(row => `${getCustomerName(row.customerId)} - ${getSiteName(row.siteId)}`)
      .join(", ");
    setGlobalErrorReport({
      showGlobalReport: true,
      topic: REPORT_TOPIC_OTHER,
      subject: t`Site package renewal`,
      description: t`Site package renewal request: ${sites}`,
    });
    onClose();
  };

  const columns = [
    {
      id: "customer",
      title: t`Customer`,
      render: (row: ISitePackageRow) => getCustomerName(row.customerId),
    },
    {
      id: "site",
      title: t`Site`,
      render: (row: ISitePackageRow) => getSiteName(row.siteId),
    },
    {
      id: "start",
      title: t`Start Date`,
      render: (row: ISitePackageRow) => formatDate(row.activePeriodStart, row.timezone),
    },
    {
      id: "end",
      title: t`End Date`,
      render: (row: ISitePackageRow) => formatDate(row.activePeriodEnd, row.timezone),
    },
    {
      id: "daysLeft",
      title: t`Days Remaining`,
      align: "center" as const,
      render: renderDaysLeft,
    },
    ...(showContactColumn
      ? [
          {
            id: "contact",
            title: t`Sales Contact Email`,
            render: (row: ISitePackageRow) =>
              row.salesAccountEmail ? (
                <ContactEmail
                  email={row.salesAccountEmail}
                  siteNames={[getSiteName(row.siteId)]}
                />
              ) : (
                t`N/A`
              ),
          },
        ]
      : []),
  ];

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      onCancel={onClose}
      onConfirm={isAlertMode ? handleContactSupport : onClose}
      hideCancel={!isAlertMode}
      cancelLabel={t`OK`}
      confirmLabel={isAlertMode ? t`Contact Support` : t`OK`}
      title={title}
      className={styles.dialog}
    >
      {isAlertMode && (
        <p className={styles.subtitle}>
          {renderRenewalMessage(
            sharedEmail && (
              <ContactEmail
                key="email"
                email={sharedEmail}
                siteNames={rows.map(row => getSiteName(row.siteId))}
              />
            )
          )}
        </p>
      )}
      <div className={styles.tableWrap}>
        <Table
          data={rows}
          getRowKey={(row: ISitePackageRow) => row.siteId}
          emptyMessage={t`No sites to show.`}
          columns={columns}
        />
      </div>
    </BaseDialog>
  );
};

export default SitePackagesDialog;
